/**
 * Light, dark, or whatever the OS says.
 *
 * Three states rather than two. A plain boolean toggle looks simpler until the
 * evening the OS flips to dark and the app does not, because at some point you
 * pressed the button — "system" has to be a state you can be in, not just the
 * state you start in.
 *
 * The resolved value lands on `<html data-theme>`, which is what styles.css keys
 * off. `index.html` sets the same attribute inline before first paint from the
 * same storage key; this hook takes over from there.
 */

import { useCallback, useEffect, useState } from 'react'

const STORAGE_KEY = 'kete-theme'
const DARK_QUERY = '(prefers-color-scheme: dark)'

export type ThemePreference = 'system' | 'light' | 'dark'
export type ResolvedTheme = 'light' | 'dark'

/** Matches the `--color-paper` / `--color-sand` the browser chrome should blend into. */
const THEME_COLOUR: Record<ResolvedTheme, string> = {
  light: '#F2EBE5',
  dark: '#15171D',
}

function readStored(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored === 'light' || stored === 'dark' ? stored : 'system'
  } catch {
    // Private mode, or storage disabled. Following the OS is the better failure.
    return 'system'
  }
}

function systemTheme(): ResolvedTheme {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light'
}

function apply(theme: ResolvedTheme): void {
  document.documentElement.dataset.theme = theme
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', THEME_COLOUR[theme])
}

export function useTheme() {
  const [preference, setPreference] = useState<ThemePreference>(readStored)
  const [resolved, setResolved] = useState<ResolvedTheme>(() =>
    preference === 'system' ? systemTheme() : preference,
  )

  useEffect(() => {
    if (preference !== 'system') {
      apply(preference)
      setResolved(preference)
      return
    }

    const media = window.matchMedia(DARK_QUERY)
    const sync = () => {
      const next = media.matches ? 'dark' : 'light'
      apply(next)
      setResolved(next)
    }

    sync()
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [preference])

  const choose = useCallback((next: ThemePreference) => {
    setPreference(next)
    try {
      if (next === 'system') localStorage.removeItem(STORAGE_KEY)
      else localStorage.setItem(STORAGE_KEY, next)
    } catch {
      // Storage being unavailable should not stop the theme changing for this
      // session; it only means the choice will not survive a reload.
    }
  }, [])

  /**
   * What the button does: step to the opposite of what you can currently see,
   * and stop following the OS. Getting back to "system" is the long-press
   * equivalent — a right-click on the toggle, handled by the caller.
   */
  const toggle = useCallback(() => {
    choose(resolved === 'dark' ? 'light' : 'dark')
  }, [choose, resolved])

  return { preference, resolved, choose, toggle }
}
